
import { supabase } from './supabaseClient';
// FIX: Import AIEditPlan and VisualPostConfig from the central types file.
import { VisualPostConfig, AIEditPlan } from '../types';

// --- INTERFACES ---

export interface BioAuditResult {
    score: number;
    strengths: string[];
    weaknesses: string[];
    suggestedBio: string;
    ctaSuggestion: string;
}

export interface ContentIdea {
    hook: string;
    format: 'REELS' | 'CAROUSEL' | 'STORY';
    description: string;
    viralScorePrediction: number;
}

export interface MarketingCampaign {
    title: string;
    emailSubject: string;
    emailBody: string;
    whatsappMessage: string;
    socialCaption: string;
    creativeIdea: string;
}

export interface CompetitorAnalysis {
    strengths: string[];
    weaknesses: string[];
    contentGaps: string[];
    attackStrategy: string;
}

export interface HashtagSet {
    niche: string[];
    viral: string[];
    location: string[];
}

export interface AutomationRuleResponse {
    trigger: string;
    responseScript: string;
}

// FIX: AIEditPlan is now imported from types.ts to resolve module errors.


// --- HELPER FUNCTION ---
// Centraliza a chamada para a Edge Function do Supabase (proxy da OpenRouter - DeepSeek)
async function callAI(prompt: string | object, isJson: boolean = false): Promise<string> {
    try {
        const { data, error } = await supabase.functions.invoke('openrouter-proxy', {
            body: { prompt, isJson } // Images removed as DeepSeek R1 is text-only
        });

        if (error) {
            console.error("Supabase Edge Function Error:", error);
            throw new Error("Erro de conexão com o servidor de IA.");
        }

        if (data.error) {
            console.error("AI Proxy Returned Error:", data.error);
            throw new Error(data.error);
        }

        if (!data.content) {
            throw new Error("Resposta vazia ou malformada da IA.");
        }

        return data.content;
    } catch (e) {
        console.error("AI Service Error:", e);
        throw e;
    }
}

// FIX: Added mock implementation for missing function to resolve import error.
export const runGrowthTool = async (id: string, context: any): Promise<string> => {
    console.log(`Running growth tool ${id} with context:`, context);
    return Promise.resolve(`AI-generated response for tool ${id}.`);
};

// FIX: Added mock implementation for missing function to resolve import error.
export const getAIVoiceSystemInstruction = (restaurantName?: string): string => {
    return `You are a helpful and friendly assistant for ${restaurantName || 'the establishment'}.`;
};

// --- CORE FUNCTIONS ---

/**
 * 11. JIRINU REAL VIDEO ANALYSIS (DEEPSEEK R1)
 */
// REFACTORED: This function now uses the generic text-only AI proxy (callAI)
// to align with the user's request to use DeepSeek R1 and fix the error.
// The `frameBase64s` parameter is kept for signature compatibility but is no longer used.
export const generateVideoEditPlan = async (
    prompt: string, 
    duration: number, 
    frameBase64s: string[], 
    language: 'pt-BR' | 'en' = 'pt-BR'
): Promise<AIEditPlan> => {
    const langInstruction = language === 'en' 
        ? "Output Language: English." 
        : "IDIOMA DE SAÍDA: PORTUGUÊS (BRASIL).";

    const systemPrompt = `You are JIRINU AI, a professional video editor. Your task is to generate a JSON Edit Decision List (EDL). The JSON MUST be perfect, contain no comments, and not be wrapped in markdown.
    
STRICT JSON FORMAT:
{
  "clips": [ { "start": number, "duration": number, "speed": number, "filter": "string" } ],
  "subtitles": [ { "text": "SHORT TEXT", "start": number, "duration": number, "style": "IMPACT" | "DEFAULT" } ],
  "narration": { "text": "A short narration script." }
}
AVAILABLE FILTERS: "none", "grayscale(1)", "contrast(1.4) brightness(0.9)", "saturate(1.5) contrast(1.1)", "sepia(0.5) contrast(0.9)".`;

    // Note: The prompt no longer mentions analyzing frames.
    const userPromptText = `Based on the video's duration and the user request, create an edit plan.
- Duration: ${Number(duration).toFixed(1)}s
- Request: "${prompt}"
- ${langInstruction}`;

    // The openrouter-proxy is updated to handle this message array structure.
    const structuredPrompt = [
        { role: "system", content: systemPrompt },
        { role: "user", content: userPromptText }
    ];

    try {
        const jsonStr = await callAI(structuredPrompt, true);
        return JSON.parse(jsonStr) as AIEditPlan;
    } catch (error: any) {
        console.error("Jirinu Video Analysis Failed:", error.message);
        // Provide a fallback response to avoid crashing the UI
        throw new Error("A IA falhou em gerar o plano de edição. Tente novamente.");
    }
};

/**
 * 1. AUDITORIA DE PERFIL INTELIGENTE
 */
export const analyzeProfileBio = async (username: string, currentBio: string, niche: string): Promise<BioAuditResult> => {
    try {
        const prompt = `Analise este perfil Instagram.
        User: @${username}
        Bio: "${currentBio}"
        Nicho: ${niche}

        Retorne JSON estrito:
        {
            "score": number (0-100),
            "strengths": string[],
            "weaknesses": string[],
            "suggestedBio": string,
            "ctaSuggestion": string
        }`;

        const jsonStr = await callAI(prompt, true);
        return JSON.parse(jsonStr) as BioAuditResult;
    } catch (e) {
        return {
            score: 0, strengths: [], weaknesses: ["Erro na análise"], suggestedBio: "Tente novamente.", ctaSuggestion: ""
        };
    }
};

/**
 * 2. GERADOR DE CONTEÚDO VIRAL
 */
export const generateViralIdeas = async (
    niche: string, 
    targetAudience: string,
    language: 'pt-BR' | 'en' = 'pt-BR'
): Promise<ContentIdea[]> => {
    try {
        const langInstruction = language === 'en' 
            ? "Output Language: English." 
            : "Idioma de Saída: Português (Brasil).";

        const prompt = `Gere 3 ideias virais (Reels/Carrossel) para nicho "${niche}", público "${targetAudience}".
        ${langInstruction}
        Retorne JSON Array:
        [
            {
                "hook": string,
                "format": "REELS" | "CAROUSEL" | "STORY",
                "description": string,
                "viralScorePrediction": number
            }
        ]`;

        const jsonStr = await callAI(prompt, true);
        return JSON.parse(jsonStr) as ContentIdea[];
    } catch (e) {
        return [];
    }
};

/**
 * 3. ANÁLISE DE CONCORRENTES
 */
export const analyzeCompetitor = async (competitorName: string, myNiche: string): Promise<CompetitorAnalysis> => {
    try {
        const prompt = `Analise o concorrente "@${competitorName}" no nicho "${myNiche}".
        Retorne JSON:
        {
            "strengths": string[],
            "weaknesses": string[],
            "contentGaps": string[],
            "attackStrategy": string
        }`;

        const jsonStr = await callAI(prompt, true);
        return JSON.parse(jsonStr) as CompetitorAnalysis;
    } catch (e) {
        throw new Error("Falha na análise");
    }
};

/**
 * 4. GERADOR DE HASHTAGS
 */
export const generateHashtags = async (topic: string, niche: string): Promise<HashtagSet> => {
    try {
        const prompt = `Hashtags para "${topic}" no nicho "${niche}".
        Retorne JSON: { "niche": string[], "viral": string[], "location": string[] }`;

        const jsonStr = await callAI(prompt, true);
        return JSON.parse(jsonStr) as HashtagSet;
    } catch (e) {
        return { niche: [], viral: [], location: [] };
    }
};

/**
 * 5. OTIMIZADOR DE LEGENDAS
 */
export const optimizeCaption = async (draft: string, tone: string): Promise<string> => {
    const prompt = `Melhore esta legenda Instagram (Tom: ${tone}): "${draft}". Retorne apenas o texto.`;
    return await callAI(prompt, false);
};

/**
 * 6. GERADOR DE AUTOMAÇÃO
 */
export const generateAutomationScript = async (goal: string, tone: string): Promise<AutomationRuleResponse> => {
    try {
        const prompt = `Script DM Instagram. Objetivo: "${goal}". Tom: "${tone}".
        Retorne JSON: { "trigger": string, "responseScript": string }`;
        const jsonStr = await callAI(prompt, true);
        return JSON.parse(jsonStr);
    } catch (e) {
        return { trigger: "EU QUERO", responseScript: "Erro ao gerar." };
    }
};

/**
 * 7. ANALISADOR DE DADOS
 */
export const getSmartInsights = async (metrics: any): Promise<string[]> => {
    try {
        const prompt = `Insights curtos sobre estas métricas: ${JSON.stringify(metrics)}. Retorne JSON Array de strings.`;
        const jsonStr = await callAI(prompt, true);
        return JSON.parse(jsonStr);
    } catch (e) {
        return ["Dados insuficientes para análise profunda."];
    }
};

/**
 * 8. ANALISE DE REELS
 */
export const analyzeReelsPerformance = async (reelsData: any[]): Promise<string> => {
    const prompt = `Analise a performance destes Reels e identifique padrões virais: ${JSON.stringify(reelsData)}. Seja estratégico.`;
    return await callAI(prompt, false);
};

/**
 * 9. AI ART DIRECTOR
 */
export const generateVisualPostDesign = async (topic: string, niche: string, brandVoice: string): Promise<VisualPostConfig> => {
    try {
        const prompt = `Crie um design de post Instagram para "${topic}" (${niche}).
        Retorne JSON:
        {
            "headline": string,
            "subheadline": string,
            "cta": string,
            "themeColor": "HEX",
            "accentColor": "HEX",
            "backgroundColor": "HEX",
            "textColor": "HEX",
            "layout": "MINIMAL_TYPO" | "IMAGE_OVERLAY" | "BOLD_FRAME" | "SPLIT_SCREEN" | "CARD_STACK" | "NEON_GLOW",
            "imageKeyword": string,
            "iconName": string
        }`;

        const jsonStr = await callAI(prompt, true);
        return JSON.parse(jsonStr) as VisualPostConfig;
    } catch (e) {
        return {
            headline: "Error", subheadline: "Retry", cta: "Retry", themeColor: "#000", accentColor: "#FFF", backgroundColor: "#FFF", textColor: "#000", layout: "MINIMAL_TYPO", imageKeyword: "ABSTRACT", iconName: "ZAP"
        };
    }
};

/**
 * 10. AI VIDEO SCRIPTING
 */
export const generateVideoCaptions = async (topic: string, duration: number): Promise<{ time: number, text: string }[]> => {
    try {
        const prompt = `Legendas temporizadas para vídeo de ${duration}s sobre "${topic}". JSON Array: [{ "time": number, "text": string }]`;
        const jsonStr = await callAI(prompt, true);
        return JSON.parse(jsonStr);
    } catch (e) {
        return [];
    }
};
